import React, { useState, useEffect } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  IconButton, 
  Menu, 
  MenuItem, 
  Avatar, 
  Divider,
  ListItemIcon,
  Tooltip,
  Badge,
  useTheme,
  List,
  ListItem,
  ListItemText,
  useMediaQuery
} from '@mui/material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ChatIcon from '@mui/icons-material/Chat';
import DescriptionIcon from '@mui/icons-material/Description';
import SearchIcon from '@mui/icons-material/Search';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import HelpIcon from '@mui/icons-material/Help';
import DataUsageIcon from '@mui/icons-material/DataUsage';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { useAuth } from '../contexts/AuthContext';
import * as userService from '../services/userService';
import { useNotifications } from '../contexts/NotificationContext';
import { useCredit } from '../contexts/CreditContext';
import FaviconIcon from './FaviconIcon';
import ProfessionalSubscriptionPopup from './ProfessionalSubscriptionPopup';

const Header: React.FC = () => {
  const { user, logout, isAdmin, canAccessFlowAdmin, refreshUserData } = useAuth();
  const { 
    notifications, 
    unreadCount, 
    markAsRead, 
    markAllAsRead, 
    deleteNotification 
  } = useNotifications();
  
  const navigate = useNavigate();
  const theme = useTheme();
  
  // Breakpoints responsivos mais granulares
  const isExtraLarge = useMediaQuery(theme.breakpoints.up('xl')); // >= 1536px
  const isLarge = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px
  const isMedium = useMediaQuery(theme.breakpoints.up('md')); // >= 900px
  const isSmall = useMediaQuery(theme.breakpoints.up('sm')); // >= 600px
  
  const { credits, shouldShowWarning, isBlocked } = useCredit();
  const [showCreditPopup, setShowCreditPopup] = useState(false);
  const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [profileMenuAnchorEl, setProfileMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [notificationsAnchorEl, setNotificationsAnchorEl] = useState<null | HTMLElement>(null);
  
  const isMobileMenuOpen = Boolean(mobileMenuAnchorEl);
  const isProfileMenuOpen = Boolean(profileMenuAnchorEl);
  const isNotificationsMenuOpen = Boolean(notificationsAnchorEl);

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMobileMenuAnchorEl(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchorEl(null);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchorEl(null);
  };

  const handleNotificationsOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationsAnchorEl(event.currentTarget);
  };

  const handleNotificationsClose = () => {
    setNotificationsAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate('/login');
  };

  // Atualizar dados do usuário periodicamente para manter saldo sincronizado
  useEffect(() => {
    if (!user) return;

    // Atualizar dados a cada 30 segundos quando o usuário está ativo
    const interval = setInterval(async () => {
      // Só atualizar se a página está visível (não está em outra aba)
      if (document.visibilityState === 'visible') {
        try {
          await refreshUserData();
        } catch (error) {
          // Silenciar erro de atualização automática
        }
      }
    }, 30000); // 30 segundos

    return () => clearInterval(interval);
  }, [user, refreshUserData]);

  const handleNavigation = (path: string) => {
    navigate(path);
    handleMobileMenuClose();
  };
  
  const handleNotificationAction = (id: string, action: string) => {
    if (action === 'read') {
      markAsRead(id);
    } else if (action === 'delete') {
      deleteNotification(id);
    } else if (action === 'navigate' && notifications) {
      const notification = notifications.find(n => n.id === id);
      if (notification && notification.action_link) {
        handleNotificationsClose();
        navigate(notification.action_link);
        markAsRead(id);
      }
    }
  };

  // Main navigation items
  const navItems = [
    { label: 'Início', path: '/dashboard', icon: <DashboardIcon /> },
    { label: 'Chat Jurídico', path: '/chat', icon: <ChatIcon /> },
    { label: 'Meus Documentos', path: '/documents', icon: <DescriptionIcon /> },
    { label: 'Pesquisar', path: '/search', icon: <SearchIcon /> },
    { label: 'Alertas', path: '/alertas', icon: <NotificationsNoneIcon /> },
    { label: 'Consumo', path: '/usage', icon: <DataUsageIcon /> },
    { label: 'Ajuda', path: '/help', icon: <HelpIcon /> },
  ];

  // Determinar quantos itens mostrar baseado no tamanho da tela
  const getVisibleItems = () => {
    if (isExtraLarge) return navItems; // Todos os itens (>= 1536px)
    if (isLarge) return navItems.slice(0, 6); // 6 itens (>= 1200px)
    if (isMedium) return navItems.slice(0, 4); // 4 itens principais (>= 900px)
    return []; // Menu hamburger para telas menores
  };

  const visibleItems = getVisibleItems();
  const hiddenItems = navItems.slice(visibleItems.length);
  
  return (
    <AppBar 
      position="sticky" 
      color="primary" 
      elevation={1}
      sx={{
        boxShadow: '0px 1px 10px rgba(0, 0, 0, 0.1)',
        width: '100%',
        maxWidth: 'none',
      }}
    >
      <Toolbar sx={{ width: '100%', maxWidth: 'none', minHeight: { xs: 56, sm: 64 } }}>
        {/* Logo */}
        <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
          <Box
            component={RouterLink}
            to={user ? "/dashboard" : "/"}
            sx={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}
          >
            <FaviconIcon 
              sx={{ 
                height: { xs: 32, sm: 40 }, 
                width: { xs: 32, sm: 40 },
                mr: { xs: 1, sm: 2 },
                borderRadius: '8px',
              }} 
            />
          </Box>
        </Box>

        <Box sx={{ flexGrow: 1 }} />

        {/* Navigation adaptável */}
        {user && (
          <>
            {/* Desktop/Tablet Navigation - Itens visíveis */}
            {isMedium && visibleItems.length > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', mx: 1 }} data-tour="nav-menu">
                {visibleItems.map((item) => (
                  item.label === 'Ajuda' ? (
                    // Ajuda: apenas ícone com tooltip
                    <Tooltip key={item.path} title="Ajuda" arrow>
                      <IconButton
                        component={RouterLink}
                        to={item.path}
                        sx={{ 
                          mx: 0.5,
                          color: '#FFFFFF',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          }
                        }}
                      >
                        {item.icon}
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip key={item.path} title={item.label} arrow>
                      <Button
                        component={RouterLink}
                        to={item.path}
                        sx={{ 
                          mx: 0.5,
                          minWidth: isLarge ? 'auto' : 48,
                          color: '#FFFFFF',
                          '&.active': {
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            fontWeight: 600
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                          }
                        }}
                        startIcon={item.icon}
                      >
                        {/* Mostrar texto apenas em telas grandes */}
                        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
                          {item.label}
                        </Box>
                      </Button>
                    </Tooltip>
                  )
                ))}
                
                {/* Menu "Mais" para itens ocultos */}
                {hiddenItems.length > 0 && (
                  <Tooltip title="Mais opções" arrow>
                    <IconButton
                      onClick={handleMobileMenuOpen}
                      sx={{ 
                        color: '#FFFFFF',
                        ml: 1,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.2)',
                        }
                      }}
                    >
                      <MenuIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )}
          </>
        )}

        {/* Right side icons */}
        {user ? (
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
            {/* Mobile menu button - só aparece em telas muito pequenas */}
            {!isMedium && (
              <IconButton
                size="large"
                aria-label="menu"
                onClick={handleMobileMenuOpen}
                sx={{ 
                  mr: 1,
                  color: '#FFFFFF',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                  }
                }}
              >
                <MenuIcon />
              </IconButton>
            )}
            
            {/* Credit Pill */}
            {user && (
              <Tooltip title={isBlocked ? 'Créditos esgotados — clique para recarregar' : shouldShowWarning ? 'Créditos baixos — clique para recarregar' : 'Seus créditos'} arrow>
                <Box
                  data-tour="credits-header"
                  onClick={() => setShowCreditPopup(true)}
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                    alignItems: 'center',
                    gap: 0.5,
                    mr: 1.5,
                    px: 1.5,
                    py: 0.5,
                    borderRadius: 2,
                    cursor: 'pointer',
                    bgcolor: isBlocked
                      ? 'rgba(211,47,47,0.18)'
                      : shouldShowWarning
                      ? 'rgba(237,108,2,0.18)'
                      : 'rgba(255,255,255,0.12)',
                    border: `1px solid ${
                      isBlocked
                        ? 'rgba(211,47,47,0.5)'
                        : shouldShowWarning
                        ? 'rgba(237,108,2,0.5)'
                        : 'rgba(255,255,255,0.25)'
                    }`,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: isBlocked
                        ? 'rgba(211,47,47,0.28)'
                        : shouldShowWarning
                        ? 'rgba(237,108,2,0.28)'
                        : 'rgba(255,255,255,0.22)',
                    },
                  }}
                >
                  <DataUsageIcon
                    sx={{
                      fontSize: 16,
                      color: isBlocked ? '#ff6b6b' : shouldShowWarning ? '#ffb74d' : 'rgba(255,255,255,0.9)',
                    }}
                  />
                  <Typography
                    variant="caption"
                    fontWeight={700}
                    sx={{
                      color: isBlocked ? '#ff6b6b' : shouldShowWarning ? '#ffb74d' : 'rgba(255,255,255,0.95)',
                      lineHeight: 1,
                    }}
                  >
                    {credits.toLocaleString()} cr
                  </Typography>
                </Box>
              </Tooltip>
            )}

            {/* Notifications */}
            <Tooltip title="Notificações" arrow>
              <IconButton
                data-tour="notifications-header"
                onClick={handleNotificationsOpen}
                aria-label={unreadCount ? `Notificações (${unreadCount} não lidas)` : 'Notificações'}
                color="secondary"
                size="large"
                sx={{ 
                  mr: 1,
                  display: { xs: isSmall ? 'flex' : 'none', sm: 'flex' }
                }}
              >
                <Badge badgeContent={unreadCount} color="error">
                  <NotificationsIcon sx={{ color: theme.palette.secondary.main }} />
                </Badge>
              </IconButton>
            </Tooltip>
            
            {/* Profile */}
            <Tooltip title="Perfil" arrow>
              <IconButton
                data-tour="profile-header"
                aria-label="Menu do perfil"
                onClick={handleProfileMenuOpen}
                edge="end"
                color="inherit"
                size="large"
              >
                <Avatar 
                  sx={{ 
                    width: { xs: 36, sm: 40 }, 
                    height: { xs: 36, sm: 40 }, 
                    bgcolor: theme.palette.primary.main,
                    cursor: 'pointer'
                  }}
                  src={user.avatar || undefined}
                  alt={user.nome_completo || 'Foto do perfil'}
                >
                  {!user.avatar && (
                    (user.firstName?.[0] || user.nome_completo?.split(' ')?.[0]?.[0] || '') + 
                    (user.lastName?.[0] || (user.nome_completo?.split(' ')?.length && user.nome_completo?.split(' ').length > 1 ? user.nome_completo?.split(' ')[1]?.[0] : '') || '')
                  )}
                </Avatar>
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              component={RouterLink} 
              to="/login" 
              variant="text"
              sx={{ 
                color: '#FFFFFF',
                mr: 1,
                display: { xs: 'none', sm: 'inline-flex' },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                }
              }}
            >
              Entrar
            </Button>
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              color="secondary"
              size={isSmall ? "medium" : "small"}
              sx={{ 
                borderRadius: '20px', 
                px: { xs: 2, sm: 3 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                '&:hover': {
                  boxShadow: '0 4px 10px rgba(224, 119, 80, 0.25)'
                }
              }}
            >
              {isSmall ? 'Cadastrar' : 'Cadastro'}
            </Button>
          </Box>
        )}
      </Toolbar>

      {/* Profile Menu */}
      <Menu
        anchorEl={profileMenuAnchorEl}
        open={isProfileMenuOpen}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: { 
            width: 220,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            mt: 1,
            '& .MuiMenuItem-root': {
              py: 1
            }
          }
        }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            {user?.firstName || user?.nome_completo || 'Usuário'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email}
          </Typography>
          {isAdmin && isAdmin() && (
            <Typography variant="caption" sx={{ 
              display: 'inline-block', 
              bgcolor: 'primary.main', 
              color: 'white', 
              px: 1, 
              py: 0.2, 
              borderRadius: 1,
              mt: 0.5
            }}>
              Administrador
            </Typography>
          )}
        </Box>
        
        <Divider />
        
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile'); }}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          Meu Perfil
        </MenuItem>
        
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/usage'); }}>
          <ListItemIcon>
            <DataUsageIcon fontSize="small" />
          </ListItemIcon>
          Consumo
        </MenuItem>
        
        <MenuItem onClick={() => { handleProfileMenuClose(); navigate('/profile?tab=settings'); }}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          Configurações
        </MenuItem>
        
        {canAccessFlowAdmin && canAccessFlowAdmin() && (
          <MenuItem 
            onClick={() => {
              handleProfileMenuClose();
              navigate('/admin');
            }}
            sx={{ 
              bgcolor: theme.palette.secondary.main, 
              color: 'white',
              my: 1,
              '&:hover': {
                bgcolor: theme.palette.secondary.light
              }
            }}
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" sx={{ color: 'white' }} />
            </ListItemIcon>
            <Typography fontWeight="bold">
              Administração
            </Typography>
          </MenuItem>
        )}
        
        <Divider />
        
        <MenuItem 
          onClick={async () => {
            handleProfileMenuClose();
            try {
              await userService.resetMyOnboarding();
              // Clear cached user profile to force fresh load
              localStorage.removeItem('userProfile');
              // Reload page to get fresh data from server
              window.location.href = '/dashboard';
            } catch (error) {
              console.error('Error resetting onboarding:', error);
            }
          }}
        >
          <ListItemIcon>
            <PlayCircleOutlineIcon fontSize="small" />
          </ListItemIcon>
          Ver Tour
        </MenuItem>
        
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sair
        </MenuItem>
      </Menu>

      {/* Notifications Menu */}
      <Menu
        anchorEl={notificationsAnchorEl}
        open={isNotificationsMenuOpen}
        onClose={handleNotificationsClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: { 
            width: 320,
            maxHeight: 400,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            mt: 1
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" fontWeight="bold">
            Notificações
          </Typography>
          {unreadCount > 0 && (
            <Tooltip title="Marcar todas como lidas">
              <IconButton size="small" onClick={() => markAllAsRead()}>
                <MarkEmailReadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        
        <Divider />
        
        {notifications && notifications.length > 0 ? (
          <List sx={{ p: 0, maxHeight: 300, overflow: 'auto' }}>
            {notifications.map((notification) => (
              <React.Fragment key={notification.id}>
                <ListItem 
                  alignItems="flex-start"
                  sx={{ 
                    px: 2, 
                    py: 1,
                    bgcolor: notification.read ? 'transparent' : 'rgba(25, 118, 210, 0.05)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.04)',
                      cursor: notification.action_link ? 'pointer' : 'default'
                    }
                  }}
                  onClick={() => notification.action_link && handleNotificationAction(notification.id, 'navigate')}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography 
                          variant="subtitle2" 
                          component="span" 
                          fontWeight={notification.read ? 'normal' : 'bold'}
                        >
                          {notification.title}
                        </Typography>
                        <Box>
                          {!notification.read && (
                            <IconButton 
                              size="small" 
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                handleNotificationAction(notification.id, 'read');
                              }}
                            >
                              <MarkEmailReadIcon fontSize="small" />
                            </IconButton>
                          )}
                          <IconButton 
                            size="small" 
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              handleNotificationAction(notification.id, 'delete');
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography
                          variant="body2"
                          color="text.primary"
                          component="span"
                        >
                          {notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="span"
                          sx={{ mt: 0.5, display: 'block' }}
                        >
                          {new Date(notification.created_at).toLocaleString()}
                        </Typography>
                      </>
                    }
                    secondaryTypographyProps={{ component: 'div' } as any}
                  />
                </ListItem>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Nenhuma notificação disponível
            </Typography>
          </Box>
        )}
      </Menu>

      {/* Mobile/More Menu */}
      <Menu
        anchorEl={mobileMenuAnchorEl}
        open={isMobileMenuOpen}
        onClose={handleMobileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: { 
            width: 280,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            mt: 1
          }
        }}
      >
        {/* Navigation Items - Mostrar todos em mobile, apenas ocultos em tablet/desktop */}
        {user && (isMedium ? hiddenItems : navItems).map((item) => (
          <MenuItem 
            key={item.path} 
            onClick={() => handleNavigation(item.path)}
            sx={{ 
              py: 1.8,
              px: 2.5,
              '&:hover': {
                bgcolor: 'primary.light',
                color: 'white'
              }
            }}
          >
            <ListItemIcon sx={{ color: 'primary.main' }}>
              {item.icon}
            </ListItemIcon>
            <ListItemText 
              primary={item.label}
              primaryTypographyProps={{
                fontWeight: 500,
                fontSize: '1rem'
              }}
            />
          </MenuItem>
        ))}
        
        {user && (
          <>
            {/* Mostrar notificações apenas em mobile (quando não há ícone de notificação visível) */}
            {!isSmall && (
              <>
                <Divider sx={{ my: 1 }} />
                
                <MenuItem 
                  onClick={() => {
                    handleMobileMenuClose();
                    handleNotificationsOpen({ currentTarget: document.body } as any);
                  }}
                  sx={{ 
                    py: 1.8,
                    px: 2.5,
                    '&:hover': {
                      bgcolor: 'warning.light',
                      color: 'text.primary'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'warning.main' }}>
                    <Badge badgeContent={unreadCount} color="error">
                      <NotificationsIcon />
                    </Badge>
                  </ListItemIcon>
                  <ListItemText 
                    primary="Notificações"
                    primaryTypographyProps={{
                      fontWeight: 500,
                      fontSize: '1rem'
                    }}
                  />
                </MenuItem>
              </>
            )}
            
            {/* Admin Button - sempre disponível no menu */}
            {canAccessFlowAdmin && canAccessFlowAdmin() && (
              <>
                <Divider sx={{ my: 1 }} />
                <MenuItem 
                  onClick={() => handleNavigation('/admin')}
                  sx={{ 
                    py: 1.8,
                    px: 2.5,
                    bgcolor: theme.palette.secondary.main,
                    color: 'white',
                    my: 1,
                    '&:hover': {
                      bgcolor: theme.palette.secondary.light,
                      color: 'white'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'white' }}>
                    <AdminPanelSettingsIcon />
                  </ListItemIcon>
                  <ListItemText 
                    primary="Administração"
                    primaryTypographyProps={{
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}
                  />
                </MenuItem>
              </>
            )}

          </>
        )}
      </Menu>

      {/* Credit Popup — triggered by credit pill click */}
      <ProfessionalSubscriptionPopup
        open={showCreditPopup}
        onClose={() => setShowCreditPopup(false)}
        isBlocked={isBlocked}
      />
    </AppBar>
  );
};

export default Header; 